import {
  cachedFigure,
  figurePath,
  isAbort,
  loadFigure,
  onFigureLoaded,
} from "@/features/data-curation/figureLoader";
import { ImageIcon, LoaderCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface ExtractionFigureImageProps {
  datasetId: string;
  rowId: string;
  columnId: string;
  figureIndex: number;
  /** The figure's path in the cell value, which lets the browser keep the image. */
  source?: string;
  caption?: string;
  className?: string;
  /** "thumb" asks for a small copy, for the grid cells. */
  size?: "full" | "thumb";
}

export const ExtractionFigureImage = ({
  datasetId,
  rowId,
  columnId,
  figureIndex,
  source,
  caption,
  className = "",
  size = "full",
}: ExtractionFigureImageProps) => {
  const path = figurePath(datasetId, rowId, columnId, figureIndex, size, source);
  const [imageUrl, setImageUrl] = useState<string | null>(() => cachedFigure(path));
  const [failed, setFailed] = useState(false);
  const [visible, setVisible] = useState(false);
  const placeholderRef = useRef<HTMLSpanElement>(null);

  // The grid preloads its figures, so a figure can arrive before it is on screen.
  useEffect(() => {
    setImageUrl(cachedFigure(path));
    setFailed(false);
    return onFigureLoaded(path, setImageUrl);
  }, [path]);

  // The figures on screen go to the front of the queue.
  useEffect(() => {
    const element = placeholderRef.current;
    if (visible || !element) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "400px" },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [visible, imageUrl, failed]);

  useEffect(() => {
    if (!visible || cachedFigure(path)) return;
    const controller = new AbortController();
    const load = async () => {
      // A shared load stops when its first caller stops, so then load again.
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const url = await loadFigure(path, controller.signal, true);
          if (!controller.signal.aborted) setImageUrl(url);
          return;
        } catch (error) {
          if (controller.signal.aborted) return;
          if (!isAbort(error)) break;
        }
      }
      setFailed(true);
    };
    void load();
    return () => controller.abort();
  }, [path, visible]);

  if (failed && !imageUrl) {
    return (
      <span
        className={`flex items-center justify-center bg-zinc-100 text-zinc-400 ${className}`}
        title="Figure unavailable"
      >
        <ImageIcon className="h-5 w-5" aria-hidden />
      </span>
    );
  }

  if (!imageUrl) {
    return (
      <span
        ref={placeholderRef}
        className={`flex items-center justify-center bg-zinc-100 text-zinc-400 ${className}`}
        aria-label="Loading figure"
      >
        <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden />
      </span>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={caption?.trim() || "Curated figure"}
      className={`object-contain ${className}`}
      decoding="async"
    />
  );
};
